import React, { useMemo } from 'react';
import FormWrapper from '../../../../Components/Form/FormWrapper';
import { useForm } from 'react-hook-form';
import SelectField from '../../../../Components/Form/components/SelectField';
import { SelectValue } from '../../../../Components/Base/Select/Select';
import SubmitButton from '../../../../Components/Button/SubmitButton';
import useTranslation from '../../../../Hooks/useTranslation';
import { CalendarViewIntervalEnum } from '../../Enums/CalendarViewInterval.enum';
import { FormValues } from '../../../MagicForm/Interfaces/FormValues';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

export interface CalendarViewSettingsValues {
	slotDuration: string;
}

export interface CalendarViewSettingsFormValues {
	slotDuration: SelectValue;
}

interface Props {
	onChange: (values: CalendarViewSettingsValues) => void;
	defaultValues?: Record<string, string | undefined>;
}

const slotDurationOptions: SelectValue[] = [
	{
		label: 'component.calendarViewSettings.slotDuration.per15minutes',
		value: CalendarViewIntervalEnum.FIFTEEN_MINUTES,
	},
	{
		label: 'component.calendarViewSettings.slotDuration.per30minutes',
		value: CalendarViewIntervalEnum.THIRTY_MINUTES,
	},
	{
		label: 'component.calendarViewSettings.slotDuration.per1hour',
		value: CalendarViewIntervalEnum.ONE_HOUR,
	},
	{
		label: 'component.calendarViewSettings.slotDuration.per2hours',
		value: CalendarViewIntervalEnum.TWO_HOURS,
	},
	{
		label: 'component.calendarViewSettings.slotDuration.per4hours',
		value: CalendarViewIntervalEnum.FOUR_HOURS,
	},
	{
		label: 'component.calendarViewSettings.slotDuration.per6hours',
		value: CalendarViewIntervalEnum.SIX_HOURS,
	},
	{
		label: 'component.calendarViewSettings.slotDuration.per12hours',
		value: CalendarViewIntervalEnum.TWELVE_HOURS,
	},
];

const CalendarViewSettings: React.FC<Props> = ({ defaultValues, onChange }) => {
	const { t } = useTranslation();

	const validationSchema = yup.object({
		slotDuration: yup.object().shape({
			label: yup.string(),
			value: yup.string(),
		}),
	});

	const defValues: Record<string, any> = useMemo(() => {
		const slotDuration = slotDurationOptions.find(
			i => i.value === defaultValues?.slotDuration,
		);
		return {
			slotDuration: slotDuration
				? {
						label: t(slotDuration.label),
						value: slotDuration.value,
				  }
				: undefined,
		};
	}, [defaultValues]);

	const formMethods = useForm<CalendarViewSettingsFormValues>({
		defaultValues: defValues,
		resolver: yupResolver(validationSchema),
	});

	const handleSubmit = (values: CalendarViewSettingsFormValues) => {
		onChange({ slotDuration: values.slotDuration.value as string });
	};

	return (
		<FormWrapper formMethods={formMethods}>
			<SelectField
				name='slotDuration'
				label='component.calendarViewSettings.slotDuration.title'
				options={slotDurationOptions}
				portal
			/>
			<div className='mt-3'>
				<SubmitButton
					onSubmit={(rawValues: FormValues) =>
						handleSubmit(rawValues as unknown as CalendarViewSettingsFormValues)
					}
					title='base.shared.save'
				/>
			</div>
		</FormWrapper>
	);
};

export default CalendarViewSettings;
